import React, { useEffect, useRef } from 'react';
import { ENTITY_LIMIT_TYPE } from '@jira/polaris__domain-project/src/entityLimitType.constants.tsx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap } from '@atlaskit/css';

import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useEntityLimitsByType } from '@jira/polaris__component-entity-limits-store/src/useEntityLimitsByType.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { LimitSectionMessage } from './LimitSectionMessage.tsx';

const viewsetsPerProject = defineMessage({
	id: 'polaris-component-limits.section-messages.viewset-per-project',
	defaultMessage:
		'Your project has reached the limit of {limit, plural, one {# section} other {# sections}}. Delete unused sections to create a new one.',
	description: 'The text displayed in the banner when section limit is reached.',
});

const viewsetsPerProjectGalaxia = defineMessage({
	id: 'polaris-component-limits.section-messages.viewset-per-project-galaxia',
	defaultMessage:
		'Your space has reached the limit of {limit, plural, one {# section} other {# sections}}. Delete unused sections to create a new one.',
	description: 'The text displayed in the banner when section limit is reached.',
});

const styles = cssMap({
	textContainer: {
		maxWidth: '180px',
	},
});

export const ViewsetLimitBanner = ({
	viewCount,
}: {
	viewCount: number;
}): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const ref = useRef(false);
	const viewsetsPerProjectLimit =
		useEntityLimitsByType(ENTITY_LIMIT_TYPE.VIEWSETS_PER_PROJECT) ?? 0;
	const limitReached = viewCount >= viewsetsPerProjectLimit;

	useEffect(() => {
		if (limitReached && !ref.current) {
			fireUIAnalytics(createAnalyticsEvent({}), 'viewsetLimitBanner shown', {
				viewCount,
			});
		} else if (!limitReached && ref.current) {
			fireUIAnalytics(createAnalyticsEvent({}), 'viewsetLimitBanner hidden', {
				viewCount,
			});
		}
		ref.current = limitReached;
	}, [limitReached, createAnalyticsEvent, viewCount]);

	if (!limitReached) {
		return null;
	}

	return (
		<LimitSectionMessage appearance="error">
			<Box xcss={styles.textContainer}>
				<Text>
					{formatMessage(
						fg('project_term_refresh_ga') ? viewsetsPerProjectGalaxia : viewsetsPerProject,
						{
							limit: viewsetsPerProjectLimit,
						},
					)}
				</Text>
			</Box>
		</LimitSectionMessage>
	);
};
